import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import type { Component } from 'vue'
import { useAuthStore } from '@/stores/auth'
import AppLayout from '@/components/layout/AppLayout.vue'
import LoginView from '@/views/LoginView.vue'
import RegisterView from '@/views/RegisterView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import { Users, FolderOpen, BookOpen, User, FileText, ClipboardList } from 'lucide-vue-next'
import { usePageLoader } from '@/composables/ui/usePageLoader'

// Types
interface NavigationItem {
  name: string
  to: string
  icon?: Component
}

interface RouteMeta {
  requiresAuth?: boolean
  sidebarVisible?: boolean
  title?: string
  icon?: Component
  roles?: string[] // Allowed roles: 'admin' or 'user'
}

// Route Configuration
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'users',
        component: () => import('@/views/UserListView.vue'),
        meta: {
          requiresAuth: true,
          title: 'User Management',
          icon: Users,
          roles: ['admin']
        }
      },
      {
        path: 'files',
        name: 'files',
        component: () => import('@/views/FileListView.vue'),
        meta: {
          requiresAuth: true,
          title: 'File Management',
          icon: FolderOpen,
          roles: ['admin']
        }
      },
      {
        path: 'materials',
        name: 'materials',
        component: () => import('@/views/MaterialView.vue'),
        meta: {
          requiresAuth: true,
          title: 'File Management',
          icon: BookOpen,
          roles: ['user']
        }
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: {
          requiresAuth: true,
          sidebarVisible: false,
          title: 'My Profile',
          icon: User,
          roles: ['user']
        }
      },
      {
        path: 'forms',
        name: 'forms',
        component: () => import('@/views/FormListView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Fill Out Forms',
          icon: FileText,
          roles: ['user']
        }
      },
      {
        path: 'forms/:slug',
        name: 'form-detail',
        component: () => import('@/views/FormDetailView.vue'),
        meta: {
          requiresAuth: true,
          sidebarVisible: false,
          title: 'Form Detail',
          roles: ['user']
        }
      },
      {
        path: 'responses',
        name: 'form-responses',
        component: () => import('@/views/FormResponsesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Form Responses',
          icon: ClipboardList,
          roles: ['user']
        }
      },
      {
        path: 'responses/:id',
        name: 'response-detail',
        component: () => import('@/views/ResponseDetailView.vue'),
        meta: {
          requiresAuth: true,
          sidebarVisible: false,
          title: 'Response Detail',
          roles: ['user']
        }
      },
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      title: 'Login'
    }
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: {
      requiresAuth: false,
      title: 'Register'
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Reset Password'
    }
  },
  {
    path: '/500',
    name: 'server-error',
    component: () => import('@/views/ServerErrorView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: {
      requiresAuth: false,
      title: 'Not Found'
    }
  }
]

// Router Instance
const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
  routes
})

// Navigation Helper
export const getNavigationItems = (routes: readonly RouteRecordRaw[]): NavigationItem[] => {
  const authStore = useAuthStore()
  const layoutRoute = routes.find(route => route.path === '/')
  if (!layoutRoute?.children) return []

  return layoutRoute.children
    .filter(route => {
      const meta = route.meta as RouteMeta
      // Check if route requires auth and is visible in sidebar
      if (!meta?.requiresAuth || route.name === 'not-found' || meta.sidebarVisible === false) {
        return false
      }
      // Check role-based access
      if (meta.roles && meta.roles.length > 0 && authStore.currentUser) {
        const userRole = authStore.currentUser.role || 'user'
        return meta.roles.includes(userRole)
      }
      // If no roles specified or user not logged in yet, show to all authenticated users
      return true
    })
    .map(route => ({
      name: (route.meta as RouteMeta)?.title || String(route.name),
      to: `/${route.path}`,
      icon: (route.meta as RouteMeta)?.icon
    }))
}

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  const pageLoader = usePageLoader()
  pageLoader.start()

  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Check if route requires authentication
  if (requiresAuth) {
    // Check if user is authenticated
    const isAuthenticated = await authStore.checkAuth()
    
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Check role-based access
    const routeMeta = to.meta as RouteMeta
    if (routeMeta.roles && routeMeta.roles.length > 0 && authStore.currentUser) {
      const userRole = authStore.currentUser.role || 'user'
      if (!routeMeta.roles.includes(userRole)) {
        // Redirect to the user's allowed default page based on role
        if (userRole === 'admin') {
          next({ name: 'users' })
        } else {
          next({ name: 'materials' })
        }
        return
      }
    }
  }

  // If user is authenticated and trying to access login page
  if (to.name === 'login' && authStore.isAuthenticated) {
    const userRole = authStore.currentUser?.role || 'user'
    if (userRole === 'admin') {
      next({ name: 'users' })
    } else {
      next({ name: 'materials' })
    }
    return
  }

  next()
})

router.afterEach(() => {
  const pageLoader = usePageLoader()
  pageLoader.stop()
})

export default router 